import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import type {
  ProductInsightsEvent,
  ProductInsightsEventParams,
  ProductInsightsEventTotals,
} from '@/lib/types';

/**
 * The Events page's one data set: EVERY event for the server-side filters
 * (cycle, format, region), pages fetched one after another up to 10 pages
 * (per page is capped at 100 upstream; a cycle is ~100 events). Beyond 1,000
 * events the set is cut and `truncated` is true. Product and search are client-side views
 * over this set, so they never refetch.
 *
 * - `totals` are the server's, for the whole set.
 * - Previous data stays on screen while a new key loads (`refreshing`);
 *   `loading` is only true when there is nothing to show yet. Loading is
 *   DERIVED from request keys, never set inside the effect.
 * - `params === null` (the cycle is not known yet) fetches nothing.
 */

const PER_PAGE = 100;
/** At most 10 pages (1,000 events) are fetched; `truncated` says more exist. */
const MAX_PAGES = 10;
export const EVENTS_FETCH_LIMIT = PER_PAGE * MAX_PAGES;

export interface EventsData {
  events: ProductInsightsEvent[];
  totals: ProductInsightsEventTotals;
  /** When EMERGE fetched it, else when the upstream generated it. */
  fetchedAt: string | null;
  /** True when the set had more than `EVENTS_FETCH_LIMIT` events and was cut. */
  truncated: boolean;
}

interface Result {
  key: string;
  data: EventsData | null;
  error: unknown | null;
}

async function fetchAll(
  params: ProductInsightsEventParams,
  signal: AbortSignal,
): Promise<EventsData> {
  const first = await api.productInsightsEvents({ ...params, page: 1, perPage: PER_PAGE }, signal);
  const totalPages = Math.max(1, first.meta.totalPages);
  const pages = Math.min(totalPages, MAX_PAGES);
  const rest: (typeof first)[] = [];
  // Sequential on purpose: an unexpectedly large set must not fan out into a
  // burst of upstream requests.
  for (let page = 2; page <= pages; page += 1) {
    if (signal.aborted) break;
    rest.push(await api.productInsightsEvents({ ...params, page, perPage: PER_PAGE }, signal));
  }
  const seen = new Set<number>();
  const events: ProductInsightsEvent[] = [];
  for (const page of [first, ...rest]) {
    for (const event of page.data.events) {
      if (event.eventId !== null) {
        if (seen.has(event.eventId)) continue;
        seen.add(event.eventId);
      }
      events.push(event);
    }
  }
  return {
    events,
    totals: first.data.totals,
    fetchedAt: first.meta.fetchedAt,
    truncated: totalPages > MAX_PAGES,
  };
}

export function useEventsData(
  params: ProductInsightsEventParams | null,
  reloadToken: number,
): { data: EventsData | null; loading: boolean; error: unknown | null; refreshing: boolean } {
  const paramsKey = params === null ? null : JSON.stringify(params);
  const requestKey = paramsKey === null ? null : `${paramsKey}#${reloadToken}`;
  const [lastData, setLastData] = useState<EventsData | null>(null);
  const [result, setResult] = useState<Result | null>(null);

  useEffect(() => {
    if (paramsKey === null) return;
    const controller = new AbortController();
    const { signal } = controller;
    const key = `${paramsKey}#${reloadToken}`;
    fetchAll(JSON.parse(paramsKey) as ProductInsightsEventParams, signal)
      .then((data) => {
        if (signal.aborted) return;
        setLastData(data);
        setResult({ key, data, error: null });
      })
      .catch((err: unknown) => {
        if (signal.aborted) return;
        setResult({ key, data: null, error: err });
      });
    return () => controller.abort();
  }, [paramsKey, reloadToken]);

  const current = result !== null && result.key === requestKey ? result : null;
  const settled = current !== null;
  return {
    data: lastData,
    loading: lastData === null && !settled,
    error: current?.error ?? null,
    refreshing: lastData !== null && !settled,
  };
}
